import React, { useState } from 'react';
import { Images, Trash2, AlertCircle, ChevronUp, ChevronDown } from 'lucide-react';
import { useAuth } from '../../../../contexts/AuthContext';
import { uploadGalleryImages, deleteGalleryImage } from '../../../../services/storageService';
import { SizeSpacingPanel, ColorsFontsPanel } from '../common/BlockStyleSettings';
import { BLOCK_FEATURES } from '../common/commonDefaults';

/**
 * MediaGalleryEditor - Editor for the media-gallery block
 * Syncs with profile's photos array, same schema as PhotoGalleryTabForm
 */
const MediaGalleryEditor = ({ block, onChange, profileId, photos = [], onUpdatePhotos, theme, hideStylePanels }) => {
  const { content } = block;
  const { currentUser } = useAuth();
  const userId = currentUser?.uid;

  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [deleting, setDeleting] = useState({});

  const handleChange = (field, value) => {
    onChange({ [field]: value });
  };

  const handleStyleChange = (styleUpdates) => {
    onChange({ style: { ...(content.style || {}), ...styleUpdates } });
  };

  // Handle file upload
  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    setUploadError('');

    // Validate
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    const maxSize = 5 * 1024 * 1024;
    const invalid = files.filter((f) => !allowedTypes.includes(f.type) || f.size > maxSize);
    if (invalid.length > 0) {
      setUploadError('Some files were invalid. Use JPEG, PNG, or WebP under 5MB.');
      return;
    }

    if (!userId || !profileId) {
      setUploadError('Profile must be saved before uploading images.');
      return;
    }

    setUploading(true);
    try {
      const results = await uploadGalleryImages(files, userId, profileId);
      const successful = results.filter((r) => r.success);

      if (successful.length > 0) {
        const newPhotos = successful.map((r) => ({
          id: r.id,
          type: 'uploaded_image',
          url: r.url,
          title: '',
          description: '',
          uploadTimestamp: Date.now(),
          order: Date.now(),
        }));
        await onUpdatePhotos([...photos, ...newPhotos]);
      }

      const failed = results.filter((r) => !r.success);
      if (failed.length > 0) {
        setUploadError(`${failed.length} file(s) failed to upload.`);
      }
    } catch (err) {
      console.error('Upload error:', err);
      setUploadError('Upload failed. Please try again.');
    } finally {
      setUploading(false);
      // Reset input
      e.target.value = '';
    }
  };

  // Reorder photo by swapping with neighbor
  const movePhoto = (index, direction) => {
    const newPhotos = [...photos];
    const targetIndex = index + direction;
    [newPhotos[index], newPhotos[targetIndex]] = [newPhotos[targetIndex], newPhotos[index]];
    onUpdatePhotos(newPhotos);
  };

  // Update a single field on a photo
  const updatePhotoField = (index, field, value) => {
    const newPhotos = [...photos];
    newPhotos[index] = { ...newPhotos[index], [field]: value };
    onUpdatePhotos(newPhotos);
  };

  // Handle delete photo
  const handleDeletePhoto = async (photoId) => {
    const photo = photos.find((p) => p.id === photoId);
    if (!photo) return;

    setDeleting((prev) => ({ ...prev, [photoId]: true }));
    try {
      if (photo.type === 'uploaded_image' && photo.url) {
        await deleteGalleryImage(photo.url, userId, profileId, false);
      }
      await onUpdatePhotos(photos.filter((p) => p.id !== photoId));
    } catch (err) {
      console.error('Delete error:', err);
    } finally {
      setDeleting((prev) => ({ ...prev, [photoId]: false }));
    }
  };

  return (
    <div className="space-y-4">
      {/* Section Heading */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">
          Section Heading
        </label>
        <input
          type="text"
          value={content.heading || ''}
          onChange={(e) => handleChange('heading', e.target.value)}
          placeholder="Photo Gallery"
          className="w-full px-3 py-2 bg-[#1a1a2e] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-[#749555] focus:outline-none"
        />
      </div>

      {/* Photo List */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium text-gray-300">
            Photos ({photos.length})
          </label>
        </div>

        {photos.length === 0 ? (
          <div className="text-center py-8 bg-[#1a1a2e] border border-gray-700 rounded-lg">
            <Images size={32} className="mx-auto text-gray-600 mb-2" />
            <p className="text-gray-500 text-sm">No photos yet</p>
            <p className="text-gray-600 text-xs">Upload images below</p>
          </div>
        ) : (
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {photos.map((photo, index) => (
              <div
                key={photo.id}
                className="p-2 rounded-lg bg-[#1a1a2e] border border-gray-700 space-y-2"
              >
                {/* Top row: thumbnail, title, reorder + delete */}
                <div className="flex items-center gap-3">
                  <img
                    src={photo.url}
                    alt={photo.title || 'Gallery photo'}
                    className="w-16 h-12 object-cover rounded flex-shrink-0"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                  <div className="flex-1 min-w-0">
                    <input
                      type="text"
                      value={photo.title || ''}
                      onChange={(e) => updatePhotoField(index, 'title', e.target.value)}
                      placeholder="Photo title..."
                      className="w-full px-2 py-1 bg-transparent border border-gray-700 rounded text-sm text-white placeholder-gray-500 focus:border-[#749555] focus:outline-none"
                    />
                  </div>
                  <div className="flex items-center gap-0.5 flex-shrink-0">
                    <button
                      onClick={() => movePhoto(index, -1)}
                      disabled={index === 0}
                      className="p-1 text-gray-400 hover:text-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                      title="Move up"
                    >
                      <ChevronUp size={14} />
                    </button>
                    <button
                      onClick={() => movePhoto(index, 1)}
                      disabled={index === photos.length - 1}
                      className="p-1 text-gray-400 hover:text-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                      title="Move down"
                    >
                      <ChevronDown size={14} />
                    </button>
                    <button
                      onClick={() => handleDeletePhoto(photo.id)}
                      disabled={deleting[photo.id]}
                      className="p-1.5 text-gray-400 hover:text-red-400 transition-colors disabled:opacity-50"
                      title="Remove photo"
                    >
                      {deleting[photo.id] ? (
                        <div className="w-3.5 h-3.5 border-2 border-red-400 border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <Trash2 size={14} />
                      )}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Upload */}
      <div className="pt-2 border-t border-gray-700">
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Add Photos
        </label>
        <label
          className={`flex items-center justify-center gap-2 w-full py-3 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
            uploading
              ? 'border-gray-600 text-gray-500 cursor-not-allowed'
              : 'border-gray-700 hover:border-[#749555] text-gray-400 hover:text-[#749555]'
          }`}
        >
          <input
            type="file"
            multiple
            accept="image/jpeg,image/jpg,image/png,image/webp"
            onChange={handleFileSelect}
            className="hidden"
            disabled={uploading}
          />
          {uploading ? (
            <>
              <div className="w-4 h-4 border-2 border-gray-600 border-t-[#749555] rounded-full animate-spin" />
              Uploading...
            </>
          ) : (
            <>
              <Images size={18} />
              Select Images
            </>
          )}
        </label>
        <p className="text-xs text-gray-500 mt-1">
          JPEG, PNG, or WebP. Up to 5MB each. Photos sync with your profile gallery.
        </p>

        {uploadError && (
          <p className="text-red-400 text-sm flex items-center gap-1 mt-2">
            <AlertCircle size={14} />
            {uploadError}
          </p>
        )}
      </div>

      {/* Gallery Settings */}
      <div className="pt-2 border-t border-gray-700 space-y-3">
        <label className="block text-sm font-medium text-gray-300">
          Gallery Settings
        </label>

        {/* Scroll Speed */}
        <div>
          <label className="block text-xs text-gray-400 mb-1">Scroll Speed</label>
          <div className="flex gap-2">
            {[
              { value: 'slow', label: 'Slow' },
              { value: 'normal', label: 'Normal' },
              { value: 'fast', label: 'Fast' },
            ].map((option) => (
              <button
                key={option.value}
                onClick={() => handleChange('autoScrollSpeed', option.value)}
                className={`flex-1 py-1.5 px-2 rounded-lg text-xs font-medium transition-colors ${
                  (content.autoScrollSpeed || 'normal') === option.value
                    ? 'bg-[#749555] text-white'
                    : 'bg-[#1a1a2e] text-gray-400 hover:text-white border border-gray-700'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {/* Image Height */}
        <div>
          <label className="block text-xs text-gray-400 mb-1">Image Height</label>
          <div className="flex gap-2">
            {[
              { value: 'small', label: 'Small' },
              { value: 'medium', label: 'Medium' },
              { value: 'large', label: 'Large' },
            ].map((option) => (
              <button
                key={option.value}
                onClick={() => handleChange('imageHeight', option.value)}
                className={`flex-1 py-1.5 px-2 rounded-lg text-xs font-medium transition-colors ${
                  (content.imageHeight || 'medium') === option.value
                    ? 'bg-[#749555] text-white'
                    : 'bg-[#1a1a2e] text-gray-400 hover:text-white border border-gray-700'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {/* Gap */}
        <div>
          <label className="block text-xs text-gray-400 mb-1">Gap</label>
          <div className="flex gap-2">
            {[
              { value: 'compact', label: 'Compact' },
              { value: 'normal', label: 'Normal' },
              { value: 'spacious', label: 'Spacious' },
            ].map((option) => (
              <button
                key={option.value}
                onClick={() => handleChange('gap', option.value)}
                className={`flex-1 py-1.5 px-2 rounded-lg text-xs font-medium transition-colors ${
                  (content.gap || 'normal') === option.value
                    ? 'bg-[#749555] text-white'
                    : 'bg-[#1a1a2e] text-gray-400 hover:text-white border border-gray-700'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {/* Max Photos */}
        <div>
          <label className="block text-xs text-gray-400 mb-1">
            Max Photos (0 = all)
          </label>
          <input
            type="number"
            min="0"
            max="50"
            value={content.maxPhotos || 0}
            onChange={(e) => handleChange('maxPhotos', parseInt(e.target.value) || 0)}
            className="w-full px-3 py-2 bg-[#1a1a2e] border border-gray-700 rounded-lg text-white focus:border-[#749555] focus:outline-none text-sm"
          />
        </div>

        {/* Pause on Hover */}
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={content.pauseOnHover !== false}
            onChange={(e) => handleChange('pauseOnHover', e.target.checked)}
            className="w-4 h-4 rounded border-gray-600 bg-[#1a1a2e] text-[#749555] focus:ring-[#749555]"
          />
          <span className="text-sm text-gray-300">Pause on Hover</span>
        </label>
      </div>

      {/* Style Panels */}
      {!hideStylePanels && (<>
      <SizeSpacingPanel
        style={content.style || {}}
        onChange={handleStyleChange}
        features={BLOCK_FEATURES['media-gallery']}
      />
      <ColorsFontsPanel
        style={content.style || {}}
        onChange={handleStyleChange}
        features={BLOCK_FEATURES['media-gallery']}
        theme={theme || {}}
        blockType="media-gallery"
      />
      </>)}
    </div>
  );
};

export default MediaGalleryEditor;
